import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { sample, trim } from "lodash-es";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom-v5-compat";
import * as z from "zod";

import {
  Button,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
} from "../../Atoms";
import { useBillSplit } from "../BillSplit/queries";
import {
  useCreateBillSplitCustomerSession,
  useCreateBillSplit,
} from "../BillSplit/queries";
import { useSession } from "../Session/SessionContext";
import { BustleBeeList } from "./bustleBees";
import { useCustomer, useSaveCustomer } from "./queries";
import { Customer } from "./schema";

const CustomerFormSchema = z.object({
  nickname: z.string().optional(),
  icon: z.string().optional(),
});
export type CustomerFormValues = z.infer<typeof CustomerFormSchema>;

const useGetUniqueNickname = (nicknames: string[]) => {
  const { t } = useTranslation();
  const defaultNameAdjectives = t("customer.defaultNameAdjectives", {
    returnObjects: true,
  });
  const bustleBeeNames = t("customer.bustleBeeNames", {
    returnObjects: true,
  }) as any;

  const nicknameFor = (adjective: string, name: string) =>
    t("customer.defaultNickname", { adjective, name });

  const getUniqueNickName = (icon: string) => () => {
    let newDefaultName: string;
    let newDefaultNameAdjective: string;
    do {
      newDefaultName = icon
        ? sample(bustleBeeNames[icon])
        : t("customer.defaultName");
      newDefaultNameAdjective =
        sample(defaultNameAdjectives) || t("customer.defaultAdjective");
    } while (
      nicknames.includes(nicknameFor(newDefaultNameAdjective, newDefaultName))
    );
    return nicknameFor(newDefaultNameAdjective, newDefaultName);
  };
  return { getUniqueNickName };
};

export const QCustomerForm = ({ customer }: { customer?: Customer | null }) => {
  const { customerTab } = useSession();
  if (!customerTab) {
    throw "no customer tab";
  }
  const { data: billSplitSession } = useBillSplit(customerTab.uuid);
  const { t } = useTranslation();

  const createCustomerSession = useCreateBillSplitCustomerSession(customerTab);
  const createSession = useCreateBillSplit(customerTab);

  const allNicknames = billSplitSession?.customers.map((c) => c.nickname) || [];
  const { getUniqueNickName } = useGetUniqueNickname(allNicknames);
  const [defaultIcon, __] = useState(sample(BustleBeeList));
  const [nicknamePlaceholder, setNicknamePlaceholder] = useState(
    getUniqueNickName(defaultIcon?.value || BustleBeeList[0].value),
  );
  const navigate = useNavigate();

  const { register, handleSubmit, ...formProps } = useForm({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues: {
      nickname: customer?.nickname,
      icon: customer?.icon || defaultIcon?.value,
    },
  });
  const saveCustomer = useSaveCustomer();

  const onSubmit = async (data: CustomerFormValues) => {
    const nickname = trim(data.nickname) || nicknamePlaceholder;
    const customer = await saveCustomer.mutateAsync({ ...data, nickname });
    if (billSplitSession) {
      await createCustomerSession.mutateAsync();
    } else {
      await createSession.mutateAsync();
    }
    navigate("..");
  };

  const updatePlaceHolderName = () => {
    const icon = formProps.getValues("icon");
    const name = formProps.getValues("nickname");
    if (name) return;
    setNicknamePlaceholder(getUniqueNickName(icon || BustleBeeList[0].value)());
  };

  return (
    <div className="mx-auto max-w-screen-sm">
      <ResponsiveTitleBar>{t("customer.addYourName")}</ResponsiveTitleBar>
      <form
        className="max-w-screen-sm space-y-32 p-16"
        onSubmit={handleSubmit(onSubmit)}
      >
        <label className="block space-y-16 text-xl font-bold">
          <div>{t("customer.nickname")}</div>
          <input
            className="block w-full rounded-venue-brand bg-venue-tertiary p-12 shadow-venue-brand-style disabled:cursor-not-allowed"
            placeholder={nicknamePlaceholder}
            {...register("nickname")}
          />
        </label>
        <div className="block space-y-16 text-xl font-bold">
          <div>{t("customer.pickYourIcon")}</div>
          <div className="mx-auto grid grid-cols-3 justify-between justify-items-center gap-32">
            {BustleBeeList.map((bee) => (
              <div key={bee.value} className="group w-min text-center">
                <input
                  className="peer hidden"
                  {...register("icon", {
                    onChange: updatePlaceHolderName,
                  })}
                  value={bee.value}
                  id={`icon[${bee.value}]`}
                  type="radio"
                />
                <label
                  htmlFor={`icon[${bee.value}]`}
                  className={classNames(
                    "peer border-venue-tertiary peer-checked:border-venue-primary",
                    "flex h-96 w-96 cursor-pointer items-center justify-center rounded-full p-12 text-4xl",
                    "border-4 bg-venue-tertiary",
                  )}
                >
                  <img src={bee.img_96} alt={bee.alt} className="" />
                </label>
              </div>
            ))}
          </div>
        </div>
        <ResponsivePrimaryAction>
          <Button primary>{t("customer.startSelecting")} </Button>
        </ResponsivePrimaryAction>
      </form>
    </div>
  );
};
export const CustomerForm = ({}: {}) => {
  const { data: customer, status } = useCustomer();
  const { t } = useTranslation();
  if (status == "pending") {
    return <div>Loading</div>;
  }
  return <QCustomerForm customer={customer} />;
};
